<mat-card appearance="outlined" class="app-content" xmlns="http://www.w3.org/1999/html">
  <mat-card-content>

    <!--
      Shared Stage-1 price breakdown for a single cart line.
      `data.item` is the cart `Item`; `data.compact` styles the table-view
      slim two-column rows when true. The block branches on
      {@link Item.getPricingState}:

        - ZERO   : quantity is 0 — render "$0.00" so the cost column
                   doesn't shift when the user is comparing rows.
        - PENDING: rates in flight — render a spinner + "Pending" label
                   so the user doesn't misread a mid-fetch `$0.00`.
        - READY  : normal case; render qty × unit + optional per-facility
                   / quantity-break tables.
        - INFEASIBLE: rates loaded but the current quantity isn't
                   priced. Render "Price unavailable for this quantity"
                   and list the ranges that DO work so the user knows
                   how to fix it.
        - ERROR : rate table is empty for this item (server returned
                   no facilities or fetch failed). Render "Price
                   unavailable" + a Retry button.

      Each conditional block is suppressed independently so an item with
      no facility variation and no quantity-break variation just shows
      the headline summary + total + weight.
    -->
    <ng-template #priceBreakdown let-data>
      <ng-container [ngSwitch]="getItemPriceState(data.item)">

        <!-- ── ZERO — quantity is 0. Show $0.00 so the column doesn't shift. ── -->
        <span *ngSwitchCase="'ZERO'" class="cart-item-cost">${{0 | number: '1.2-2'}}</span>

        <!-- ── PENDING — rates loading. Spinner + label instead of $0.00. ── -->
        <ng-container *ngSwitchCase="'PENDING'">
          <span class="cart-item-cost-pending">
            <mat-progress-spinner mode="indeterminate" diameter="16" color="primary"></mat-progress-spinner>
            <span i18n="@@Calculating price…">Calculating price…</span>
          </span>
        </ng-container>

        <!-- ── ERROR — no rate table at all. Retry + explanation. ── -->
        <ng-container *ngSwitchCase="'ERROR'">
          <span class="cart-item-cost-error" i18n="@@Price unavailable">Price unavailable</span>
          <span class="cart-item-cost-error-hint" i18n="@@We couldnt get pricing for this item.">We couldn't get pricing for this item.</span>
          <button mat-stroked-button type="button" class="cart-item-retry" (click)="retryItemPricing(data.item)">
            <mat-icon>refresh</mat-icon><span i18n="@@Retry">Retry</span>
          </button>
        </ng-container>

        <!-- ── INFEASIBLE — no rate at THIS quantity; some other qty works. ── -->
        <ng-container *ngSwitchCase="'INFEASIBLE'">
          <span class="cart-item-cost-error" i18n="@@No price at this quantity">No price at this quantity</span>
          <span class="cart-item-cost-error-hint" i18n="@@Adjust the quantity to one of these ranges:">Adjust the quantity to one of these ranges:</span>
          <table class="cart-item-quantity-breaks cart-item-quantity-breaks-infeasible">
            <thead>
              <tr>
                <th i18n="@@Qty">Qty</th>
                <th i18n="@@Unit">Unit</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let br of data.item.getFeasibleQuantityRanges()">
                <td>
                  <ng-container *ngIf="br.maxQty < Number.MAX_SAFE_INTEGER && br.maxQty > br.minQty">{{br.minQty}}&ndash;{{br.maxQty}}</ng-container>
                  <ng-container *ngIf="br.maxQty < Number.MAX_SAFE_INTEGER && br.maxQty === br.minQty">{{br.minQty}}</ng-container>
                  <ng-container *ngIf="br.maxQty >= Number.MAX_SAFE_INTEGER">{{br.minQty}}+</ng-container>
                </td>
                <td>${{br.unitCost | number: '1.2-2'}}</td>
              </tr>
            </tbody>
          </table>
        </ng-container>

        <!-- ── READY — the normal case. Full price breakdown. ── -->
        <ng-container *ngSwitchCase="'READY'">
          <!-- Headline summary line: qty × $unit/ea (+ setup if any) -->
          <span class="cart-item-cost-summary">
            {{data.item.quantity}} &times; ${{data.item.getUnitCost() | number: '1.2-2'}}<span i18n="@@/ea">/ea</span><!--
         --><ng-container *ngIf="data.item.getPerOrderCost && data.item.getPerOrderCost() > 0">
              &nbsp;+&nbsp;${{data.item.getPerOrderCost() | number: '1.2-2'}}&nbsp;<ng-container i18n="@@setup">setup</ng-container>
            </ng-container>
          </span>
          <!-- Line total -->
          <span class="cart-item-cost">${{data.item.cost | number: '1.2-2'}}</span>
          <!-- Line weight -->
          <span class="cart-item-weight">{{(data.item.getWeight() * data.item.getQuantity()) | measureWeight:1}}</span>

          <!-- Per-facility cost breakdown (only when facilities differ in price) -->
          <table class="cart-item-facility-breakdown"
                 *ngIf="data.item.getFacilityCostBreakdown && data.item.getFacilityCostBreakdown().length > 1">
            <thead>
              <tr>
                <th i18n="@@Facility">Facility</th>
                <th i18n="@@Unit">Unit</th>
                <th i18n="grand total|The final amount including shipping, handling and tax. Must be distinguishable from Subtotal.@@Total">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let fc of data.item.getFacilityCostBreakdown()"
                  [class.cart-item-facility-row-current]="fc.facilityId === data.item.costFacilityId">
                <td>{{facilityFriendlyNames['' + fc.facilityId] || fc.facilityName || ('Facility ' + fc.facilityId)}}</td>
                <td>${{fc.unitCost | number: '1.2-2'}}</td>
                <td>${{fc.totalCost | number: '1.2-2'}}</td>
              </tr>
            </tbody>
          </table>

          <!-- Quantity-break ladder (only when the costing facility has multiple breaks) -->
          <table class="cart-item-quantity-breaks"
                 *ngIf="data.item.getCurrentFacilityQuantityBreaks && data.item.getCurrentFacilityQuantityBreaks().length > 1">
            <thead>
              <tr>
                <th i18n="@@Qty">Qty</th>
                <th i18n="@@Unit">Unit</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let br of data.item.getCurrentFacilityQuantityBreaks()"
                  [class.cart-item-quantity-break-current]="br.isCurrent">
                <td>
                  <ng-container *ngIf="br.maxQty < Number.MAX_SAFE_INTEGER && br.maxQty > br.minQty">{{br.minQty}}&ndash;{{br.maxQty}}</ng-container>
                  <ng-container *ngIf="br.maxQty < Number.MAX_SAFE_INTEGER && br.maxQty === br.minQty">{{br.minQty}}</ng-container>
                  <ng-container *ngIf="br.maxQty >= Number.MAX_SAFE_INTEGER">{{br.minQty}}+</ng-container>
                </td>
                <td>${{br.unitCost | number: '1.2-2'}}</td>
              </tr>
            </tbody>
          </table>
        </ng-container>

      </ng-container>
    </ng-template>

    <div class="cart-page-container">
      <!-- ═══ EMPTY CART ═══ -->
      <!--
        The button used to read "Add books to your library" and
        carry `[routerLink]="['/secure/books']"` — a route that has
        never been registered in `app-routing.module.ts`, so it
        navigated nowhere and the operator was left with a dead
        button on the one screen that was trying to help them.

        There is no "library" in this app either; content lives in
        Projects. So the empty cart now offers the same Create
        Project flow the Projects screen does, which (by default)
        also starts the Draft where the title, author and the
        interior / cover PDFs are entered.
      -->
      <div class="cart-empty" *ngIf="cart && cart.items && cart.items.length === 0">
        <mat-icon class="cart-empty-icon">shopping_cart</mat-icon>
        <p class="cart-empty-text" i18n="@@You have no items in your cart.">You have no items in your cart.</p>
        <p class="cart-empty-detail" *ngIf="hasNothingToOrder" i18n="@@cartEmptyNoContentDetail">
          You do not have any Books, Drafts or Projects yet. Create a Project to enter your title and author and upload your interior and cover PDFs.
        </p>
        <button mat-raised-button color="primary" (click)="createProject()">
          <mat-icon>add</mat-icon>
          <span i18n="@@Create Project">Create Project</span>
        </button>
      </div>

      <ng-container [ngSwitch]="mode">
        <!-- ═══ CART MODE ═══ -->
        <ng-container *ngSwitchCase="'CART'">
          <div class="cart" *ngIf="cart && cart.items && cart.items.length > 0">

            <!-- ── Summary Bar ── -->
            <div class="cart-summary-bar" [class.cart-summary-bar-expanded]="summaryExpanded">
              <div class="cart-summary-stats">
                <div class="cart-summary-stat">
                  <span class="cart-summary-value">{{totalQuantity}}</span>
                  <span class="cart-summary-label" i18n="@@items">items</span>
                </div>
                <div class="cart-summary-stat" *ngIf="totalBookCount > 0">
                  <mat-icon class="cart-summary-icon book-icon">menu_book</mat-icon>
                  <span class="cart-summary-value">{{totalBookQuantity}}</span>
                  <span class="cart-summary-label" i18n="@@books">books</span>
                </div>
                <div class="cart-summary-stat" *ngIf="totalDraftCount > 0">
                  <mat-icon class="cart-summary-icon draft-icon">edit_note</mat-icon>
                  <span class="cart-summary-value">{{totalDraftQuantity}}</span>
                  <span class="cart-summary-label" i18n="@@drafts">drafts</span>
                </div>
                <div class="cart-summary-divider"></div>
                <div class="cart-summary-stat">
                  <span class="cart-summary-value">{{totalWeight | number: '1.1-1'}}</span>
                  <span class="cart-summary-label" i18n="@@lbs">lbs</span>
                </div>
                <div class="cart-summary-divider"></div>
                <div class="cart-summary-stat cart-summary-total">
                  <!-- Bind to the component's `totalCost` (kept fresh by
                       `verifyCostsAndShipping()` on every quantity /
                       rate-arrival change) rather than `cart.totalCost`
                       (which the Cart model only refreshes from its own
                       `verifyCostsAndShipping()` — never called from
                       this page, so it lags behind). Suppresses the
                       amount when at least one non-zero item is still
                       waiting on its rates so the user doesn't misread
                       an in-progress `$0.00` running total as the final
                       price. -->
                  <ng-container *ngIf="!anyItemPricingPending; else summaryTotalPending">
                    <span class="cart-summary-value">${{totalCost | number: '1.2-2'}}</span>
                  </ng-container>
                  <ng-template #summaryTotalPending>
                    <span class="cart-summary-value cart-summary-value-pending">
                      <mat-progress-spinner mode="indeterminate" diameter="16" color="primary"></mat-progress-spinner>
                      <span i18n="@@Calculating…">Calculating…</span>
                    </span>
                  </ng-template>
                </div>
              </div>
              <span *ngIf="totalQuantity === 0" class="cart-checkout-hint">
                <mat-icon>info_outline</mat-icon>
                <span i18n="@@Set item quantities to enable checkout">Set item quantities to enable checkout</span>
              </span>
              <span *ngIf="totalQuantity > 0 && anyItemPricingErrored" class="cart-checkout-hint cart-checkout-hint-error">
                <mat-icon>error_outline</mat-icon>
                <span i18n="@@Fix the highlighted items to enable checkout — adjust quantity or click Retry.">Fix the highlighted items to enable checkout — adjust quantity or click Retry.</span>
              </span>
              <span *ngIf="totalQuantity > 0 && !anyItemPricingErrored && anyItemPricingPending" class="cart-checkout-hint">
                <mat-icon>hourglass_empty</mat-icon>
                <span i18n="@@Waiting for prices…">Waiting for prices…</span>
              </span>

              <!-- ── Expand/collapse control ── Toggles the read-only
                   per-item summary panel below. Mirrors the Order
                   Summary layout used at the top of the checkout
                   stage so the two views feel consistent. -->
              <button *ngIf="totalQuantity > 0"
                      class="cart-summary-toggle"
                      type="button"
                      (click)="summaryExpanded = !summaryExpanded"
                      [attr.aria-expanded]="summaryExpanded"
                      [attr.aria-label]="summaryExpanded ? 'Hide item summary' : 'Show item summary'"
                      i18n-title="@@Show/Hide item summary" title="Show/Hide item summary">
                <mat-icon>{{ summaryExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                <span class="toolbar-btn-label" *ngIf="!summaryExpanded" i18n="@@Show items">Show items</span>
                <span class="toolbar-btn-label" *ngIf="summaryExpanded" i18n="@@Hide items">Hide items</span>
              </button>

              <!-- ── Non-zero items panel (collapsible, read-only) ──
                   Compact table listing every item currently in the
                   cart (quantity > 0). Same columns as the checkout
                   Order Summary so users see the same shape they'll
                   confirm at stage 2 — but here it's purely
                   informational; quantities are edited in the row
                   list below. Filter by `item.quantity > 0` inline
                   rather than relying on `cart.nonZeroItems`, which
                   is only refreshed by certain code paths. -->
              <div class="cart-summary-items" *ngIf="summaryExpanded">
                <div class="cart-summary-items-header">
                  <span class="cart-summary-col-id" i18n="@@ID">Id</span>
                  <span class="cart-summary-col-title" i18n="@@Title">Title</span>
                  <span class="cart-summary-col-qty" i18n="@@Qty">Qty</span>
                  <span class="cart-summary-col-weight" i18n="@@Weight">Weight</span>
                  <span class="cart-summary-col-cost" i18n="what we pay|Internal cost to produce or ship the item. Not the customer-facing Price. Confusing the two misreports margin.@@Cost">Cost</span>
                </div>
                <ng-container *ngFor="let item of cart.items">
                  <div class="cart-summary-items-row" *ngIf="item.quantity > 0">
                    <span class="cart-summary-col-id">
                      <mat-icon class="cart-summary-row-type-icon"
                                *ngIf="isInstanceOf(item, 'ProductItem')"
                                i18n-title="@@Book" title="Book">menu_book</mat-icon>
                      <mat-icon class="cart-summary-row-type-icon"
                                *ngIf="isInstanceOf(item, 'DraftItem')"
                                i18n-title="unfinished book record|A book that is still being edited and has not been finalised. A distinct entity from a Project, which is a folder grouping Books and Drafts.@@Draft" title="Draft">edit_note</mat-icon>
                      {{item.getId()}}
                    </span>
                    <span class="cart-summary-col-title" [title]="item.getTitle()">{{item.getTitle()}}</span>
                    <span class="cart-summary-col-qty">{{item.quantity}}</span>
                    <span class="cart-summary-col-weight">{{(item.getWeight() * item.quantity) | measureWeight:1}}</span>
                    <span class="cart-summary-col-cost">
                      <ng-container [ngSwitch]="getItemPriceState(item)">
                        <ng-container *ngSwitchCase="'READY'">${{item.getCost() | number: '1.2-2'}}</ng-container>
                        <span *ngSwitchCase="'ERROR'" class="cart-summary-cost-pending" i18n="@@Price unavailable">Price unavailable</span>
                        <span *ngSwitchCase="'INFEASIBLE'" class="cart-summary-cost-pending" i18n="@@No price at this quantity">No price at this quantity</span>
                        <span *ngSwitchDefault class="cart-summary-cost-pending" i18n="@@Pending">Pending</span>
                      </ng-container>
                    </span>
                  </div>
                </ng-container>
              </div>
            </div>

            <!-- ── Search indicator ── -->
            <div *ngIf="searchString" class="cart-search-indicator">
              <mat-icon>search</mat-icon><span i18n="@@Search results for ">Search results for </span><span>"{{searchString.toLowerCase()}}"</span>
            </div>

            <!-- ── Card View ── -->
            <div class="cart-items-grid" *ngIf="narrow || !showTable">
              <ng-container *ngFor="let item of cart.items">
                <div class="cart-item-card"
                     *ngIf="itemMatchesFilters(item)"
                     [class.cart-item-book]="isInstanceOf(item, 'ProductItem')"
                     [class.cart-item-draft]="isInstanceOf(item, 'DraftItem')"
                     [class.cart-item-in-cart]="item.quantity > 0"
                     [class.cart-item-blocking]="item.quantity > 0 && (getItemPriceState(item) === 'ERROR' || getItemPriceState(item) === 'INFEASIBLE')">
                  <!-- Type badge -->
                  <div class="cart-item-type-badge" [class.book-badge]="isInstanceOf(item, 'ProductItem')" [class.draft-badge]="isInstanceOf(item, 'DraftItem')">
                    <mat-icon *ngIf="isInstanceOf(item, 'ProductItem')">menu_book</mat-icon>
                    <mat-icon *ngIf="isInstanceOf(item, 'DraftItem')">edit_note</mat-icon>
                  </div>
                  <!-- Cover image -->
                  <div class="cart-item-cover" *ngIf="showCoverImage">
                    <app-project-book-cover-image *ngIf="isInstanceOf(item, 'ProductItem') && item.getBase().book" [book]="item.getBase().book" [width]="200" [height]="200"></app-project-book-cover-image>
                    <app-project-draft-cover-image *ngIf="isInstanceOf(item, 'DraftItem')" [draft]="item.getBase()" [width]="200" [height]="200"></app-project-draft-cover-image>
                    <mat-icon *ngIf="isInstanceOf(item, 'ProductItem') && !item.getBase().book" class="cart-cover-placeholder">menu_book</mat-icon>
                  </div>
                  <!-- Content -->
                  <div class="cart-item-content">
                    <div class="cart-item-title">{{item.getTitle()}}</div>
                    <div class="cart-item-meta">
                      <span>{{item.getLastUpdate() | date}}</span>
                      <span *ngIf="getFolder(item)" class="cart-item-folder" title="Folder" i18n-title="@@Folder">
                        <mat-icon>folder</mat-icon>{{getFolder(item)}}
                      </span>
                    </div>
                    <div *ngIf="showDetails" class="cart-item-details">
                      <span class="cart-detail-chip">{{item.getInteriorType() | languageString : 'compressSpaces'}}</span>
                      <span class="cart-detail-chip">{{item.getBindingType() | languageString}}</span>
                      <span class="cart-detail-chip">{{item.getLaminateType() | languageString : 'compressSpaces'}}</span>
                      <span class="cart-detail-chip">{{item.getWidth() | measureLengthValue:2}}&times;{{item.getHeight() | measureLength:2}}</span>
                      <span class="cart-detail-chip">{{item.getPages()}} <span i18n="abbreviation for pages|Short form of &quot;pages&quot;, shown after a page count, e.g. &quot;248 pg&quot;.@@pg">pg</span></span>
                      <ng-container *ngFor="let availableInventory of item.getAvailableInventory()">
                        <ng-container *ngIf="availableInventory['quantity'] >= item.quantity">
                          <span class="cart-detail-chip cart-detail-inventory">{{availableInventory['facility']}}: {{availableInventory['quantity']}}</span>
                        </ng-container>
                      </ng-container>
                    </div>
                  </div>
                  <!-- Quantity + Price footer -->
                  <div class="cart-item-footer">
                    <div class="number-input">
                      <button type="button" class="minus" (click)="decrementQuantity(item)"></button>
                      <input min="0" type="number" max="2000" class="quantity" [value]="item.quantity" (input)="onQuantityInput(item, $event)">
                      <button type="button" class="plus" (click)="incrementQuantity(item)"></button>
                    </div>
                    <div class="cart-item-price">
                      <ng-container *ngTemplateOutlet="priceBreakdown; context: { $implicit: { item: item, compact: false } }"></ng-container>
                    </div>
                  </div>
                </div>
              </ng-container>
            </div>

            <!-- ── Table View ── -->
            <div class="cart-items-table-wrapper" *ngIf="!(narrow || !showTable)">
              <table class="cart-table">
                <thead>
                  <tr>
                    <th></th>
                    <th i18n="@@ITEM DESCRIPTION">ITEM DESCRIPTION</th>
                    <th i18n="@@QUANTITY">Quantity</th>
                    <th i18n="@@COST">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <ng-container *ngFor="let item of cart.items">
                    <tr *ngIf="itemMatchesFilters(item)"
                        class="cart-table-row"
                        [class.cart-item-book]="isInstanceOf(item, 'ProductItem')"
                        [class.cart-item-draft]="isInstanceOf(item, 'DraftItem')"
                        [class.cart-item-in-cart]="item.quantity > 0"
                        [class.cart-item-blocking]="item.quantity > 0 && (getItemPriceState(item) === 'ERROR' || getItemPriceState(item) === 'INFEASIBLE')">
                      <td class="cart-table-type">
                        <mat-icon *ngIf="isInstanceOf(item, 'ProductItem')" class="book-icon" i18n-title="@@Book" title="Book">menu_book</mat-icon>
                        <mat-icon *ngIf="isInstanceOf(item, 'DraftItem')" class="draft-icon" i18n-title="unfinished book record|A book that is still being edited and has not been finalised. A distinct entity from a Project, which is a folder grouping Books and Drafts.@@Draft" title="Draft">edit_note</mat-icon>
                      </td>
                      <td class="cart-table-desc">
                        <div class="cart-table-desc-row">
                          <div *ngIf="showDetails && showCoverImage" class="cart-table-cover">
                            <app-project-book-cover-image *ngIf="isInstanceOf(item, 'ProductItem') && item.getBase().book" [book]="item.getBase().book" [width]="80" [height]="110"></app-project-book-cover-image>
                            <app-project-draft-cover-image *ngIf="isInstanceOf(item, 'DraftItem')" [draft]="item.getBase()" [width]="80" [height]="110"></app-project-draft-cover-image>
                          </div>
                          <div class="cart-table-info">
                            <div class="cart-item-title">{{item.getTitle()}}</div>
                            <div class="cart-item-meta">
                              <span>{{item.getLastUpdate() | date}}</span>
                              <span *ngIf="getFolder(item)" class="cart-item-folder" title="Folder" i18n-title="@@Folder">
                                <mat-icon>folder</mat-icon>{{getFolder(item)}}
                              </span>
                            </div>
                            <div *ngIf="showDetails" class="cart-item-details">
                              <span class="cart-detail-chip">{{item.getInteriorType() | languageString : 'compressSpaces'}}</span>
                              <span class="cart-detail-chip">{{item.getBindingType() | languageString : 'compressSpaces'}}</span>
                              <span class="cart-detail-chip">{{item.getLaminateType() | languageString}}</span>
                              <span class="cart-detail-chip">{{item.getWidth() | measureLengthValue:2}}&times;{{item.getHeight() | measureLength:2}}</span>
                              <span class="cart-detail-chip">{{item.getPages()}} <span i18n="abbreviation for pages|Short form of &quot;pages&quot;, shown after a page count, e.g. &quot;248 pg&quot;.@@pg">pg</span></span>
                              <span class="cart-detail-chip" *ngIf="item.quantity > 0">{{(item.getWeight() * item.getQuantity()) | measureWeight:1}}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td class="cart-table-qty">
                        <div class="number-input">
                          <button type="button" class="minus" (click)="decrementQuantity(item)"></button>
                          <input min="0" type="number" max="2000" class="quantity" [value]="item.quantity" (input)="onQuantityInput(item, $event)">
                          <button type="button" class="plus" (click)="incrementQuantity(item)"></button>
                        </div>
                      </td>
                      <td class="cart-table-cost">
                        <ng-container *ngTemplateOutlet="priceBreakdown; context: { $implicit: { item: item, compact: true } }"></ng-container>
                      </td>
                    </tr>
                  </ng-container>
                </tbody>
              </table>
            </div>

          </div>
        </ng-container>

        <!-- ═══ CHECKOUT MODE ═══ -->
        <ng-container *ngSwitchCase="'CHECKOUT'">
          <div class="checkout-container" [class.checkout-narrow]="narrow">

            <!-- ── Order Summary (always visible, not in toolbar — good for mobile) ── -->
            <div class="checkout-summary">
              <h3 class="checkout-section-title" i18n="@@Order Summary">Order Summary</h3>
              <!-- Item line items -->
              <div class="checkout-summary-items">
                <div class="checkout-summary-item-header">
                  <span class="checkout-col-qty" i18n="@@Qty">Qty</span>
                  <span class="checkout-col-title" i18n="@@Title">Title</span>
                  <span class="checkout-col-type" i18n="@@Type">Type</span>
                  <span class="checkout-col-weight" i18n="@@Weight">Weight</span>
                  <span class="checkout-col-cost" i18n="what we pay|Internal cost to produce or ship the item. Not the customer-facing Price. Confusing the two misreports margin.@@Cost">Cost</span>
                </div>
                <div class="checkout-summary-item" *ngFor="let item of cart.nonZeroItems">
                  <span class="checkout-col-qty">{{item.quantity}}</span>
                  <span class="checkout-col-title">{{item.getTitle()}}</span>
                  <span class="checkout-col-type">{{isInstanceOf(item, 'ProductItem') ? 'Book' : 'Draft'}}</span>
                  <span class="checkout-col-weight">{{(item.getWeight() * item.quantity) | measureWeight:1}}</span>
                  <span class="checkout-col-cost">
                    <ng-container [ngSwitch]="getItemPriceState(item)">
                      <ng-container *ngSwitchCase="'READY'">${{item.getCost() | number: '1.2-2'}}</ng-container>
                      <span *ngSwitchCase="'ERROR'" class="checkout-pending" i18n="@@Price unavailable">Price unavailable</span>
                      <span *ngSwitchDefault class="checkout-pending" i18n="@@Pending">Pending</span>
                    </ng-container>
                  </span>
                </div>
              </div>
              <!-- Items total row (aligned with columns above) -->
              <div class="checkout-summary-item checkout-summary-item-total">
                <span class="checkout-col-qty"></span>
                <span class="checkout-col-title">{{totalQuantity}} <ng-container i18n="@@titles">titles</ng-container></span>
                <span class="checkout-col-type"></span>
                <span class="checkout-col-weight">{{totalWeight | measureWeight:1}}</span>
                <span class="checkout-col-cost">
                  <ng-container *ngIf="!anyItemPricingPending; else itemsTotalPending">${{totalCost | number: '1.2-2'}}</ng-container>
                  <ng-template #itemsTotalPending>
                    <span class="checkout-pending" i18n="@@Pending">Pending</span>
                  </ng-template>
                </span>
              </div>
              <hr class="checkout-summary-divider">
              <!-- Cost breakdown -->
              <!-- Cost breakdown.
                   Gate the numeric display on `cart.selectedOrderOption`
                   rather than on `shippingReady`: the latter only flips
                   after the user *manually* clicks a shipping row (auto-
                   selected defaults deliberately keep it false so the
                   accordion doesn't collapse — see
                   `CartShippingSectionComponent.maybeAutoSelectChoice`).
                   Previously the summary showed "Pending" indefinitely
                   for anyone who accepted their org's default shipping
                   without touching the radio, even though the cart's
                   `shippingCost` / `packingAndPackagingCost` were
                   already populated (bug 2026-07-20 pass 4). -->
              <div class="checkout-summary-grid">
                <div class="checkout-summary-row"><span i18n="@@Packing and Packaging">Packing and Packaging</span><span *ngIf="cart.selectedOrderOption">${{cart.packingAndPackagingCost | number: '1.2-2'}}</span><span *ngIf="!cart.selectedOrderOption" class="checkout-pending" i18n="@@Pending">Pending</span></div>
                <div class="checkout-summary-row"><span i18n="outbound delivery|Delivery of the order to the customer. Not Billing, and not the freight cost line.@@Shipping">Shipping</span><span *ngIf="cart.selectedOrderOption">${{cart.shippingCost | number: '1.2-2'}}</span><span *ngIf="!cart.selectedOrderOption" class="checkout-pending" i18n="@@Pending">Pending</span></div>
                <div class="checkout-summary-row"><span i18n="@@Tax">Tax</span><span *ngIf="salesTaxReady">${{cart.taxCost | number: '1.2-2'}}</span><span *ngIf="!salesTaxReady" class="checkout-pending" i18n="@@Pending">Pending</span></div>
                <hr class="checkout-summary-divider">
                <div class="checkout-summary-row checkout-summary-total">
                  <span i18n="@@Order total">Order total</span>
                  <span>
                    ${{totalCost + (cart.selectedOrderOption ? cart.shippingCost + cart.packingAndPackagingCost : 0) + (salesTaxReady ? cart.taxCost : 0) | number: '1.2-2'}}
                    <span *ngIf="anyItemPricingPending || !cart.selectedOrderOption || !salesTaxReady" class="checkout-pending-note" i18n="@@excludes pending charges"> (excludes pending charges)</span>
                  </span>
                </div>
              </div>
              <!-- Production facilities -->
              <div *ngIf="cart.facilities && cart.facilities.length > 0" class="checkout-facilities">
                <span class="checkout-facilities-label" i18n="@@Production locations">Production locations</span>
                <span *ngFor="let facility of cart.facilities; let last = last">{{facilityFriendlyNames['' + facility.ID] || facility.name}}<ng-container *ngIf="!last">, </ng-container></span>
              </div>
              <button class="cart-checkout-btn checkout-place-order-btn" [disabled]="!(addressReady && shippingSelectionMade && paymentReady)" (click)="invoice && cart.invoiceable ? placeInvoicedOrder() : makePurchase()">
                <mat-icon>lock</mat-icon>
                <ng-container *ngIf="!invoice || !cart.invoiceable" i18n="@@Place your credit card order">Place your credit card order</ng-container>
                <ng-container *ngIf="invoice && cart.invoiceable" i18n="@@Place your invoice order">Place your invoice order</ng-container>
              </button>
            </div>

            <!-- ── Checkout accordion sections ── -->
            <!--
              Each section was extracted into its own component
              (2026-06-24) so cart-page is purely the checkout
              orchestrator: it owns the Cart, the readiness flags, and
              the `checkAddressIsComplete()` / `verifyPaymentInfo()`
              cross-cutting handlers; the children render the markup,
              own their section-local form/validation logic, and emit
              field-change events the parent listens for.
            -->
            <div class="checkout-sections">
              <mat-accordion multi>

                <app-cart-address-section
                  [cart]="cart"
                  [addresses]="addresses"
                  [addressReady]="addressReady"
                  [(userControlledAccordion)]="userControlledAccordion"
                  [(editDeliveryAddress)]="editDeliveryAddress"
                  [(deliveryAddressChanged)]="deliveryAddressChanged"
                  [(validatedAddress)]="validatedAddress"
                  (addressesChange)="onAddressesChange($event)"
                  (addressInfoChanged)="checkAddressIsComplete()">
                </app-cart-address-section>

                <app-cart-shipping-section
                  [cart]="cart"
                  [addressReady]="addressReady"
                  [shippingReady]="shippingReady"
                  [(userControlledAccordion)]="userControlledAccordion"
                  [facilityFriendlyNames]="facilityFriendlyNames"
                  (shippingChoicePicked)="onShippingChoicePicked($event)"
                  (shippingChoiceAutoSelected)="onShippingChoiceAutoSelected($event)">
                </app-cart-shipping-section>

                <app-cart-payment-section
                  [cart]="cart"
                  [addressReady]="addressReady"
                  [shippingReady]="shippingReady"
                  [paymentReady]="paymentReady"
                  [(userControlledAccordion)]="userControlledAccordion"
                  [(enterCreditCard)]="enterCreditCard"
                  [(invoice)]="invoice"
                  [(billingAddressTheSameAsShipping)]="billingAddressTheSameAsShipping"
                  [(savePaymentInfoAsChargeProfile)]="savePaymentInfoAsChargeProfile"
                  [(savePaymentInfoAsChargeProfileName)]="savePaymentInfoAsChargeProfileName"
                  (paymentInfoChanged)="verifyPaymentInfo()">
                </app-cart-payment-section>

              </mat-accordion>
            </div>
          </div>
        </ng-container>

        <!-- ═══ CONFIRMATION MODE ═══ -->
        <ng-container *ngSwitchCase="'CONFIRMATION'">
          <div class="checkout-confirmation">
            <ng-container *ngIf="createdOrderDeliverTo">
              <mat-icon class="confirmation-icon">check_circle</mat-icon>
              <h2 i18n="@@Order Created">Order Created</h2>
              <p><span i18n="@@Order Id">Order Id</span>: {{createdOrderDeliverTo.order_ID}}</p>
            </ng-container>
            <ng-container *ngIf="!createdOrderDeliverTo">
              <mat-progress-spinner color="primary" mode="indeterminate" diameter="48"></mat-progress-spinner>
              <h2 i18n="@@We are creating your order.  Please wait.">We are creating your order. Please wait.</h2>
            </ng-container>
          </div>
        </ng-container>
      </ng-container>


    </div>
  </mat-card-content>
</mat-card>
